import { InjectableClass } from '@fino/lib-injection';
import { defaults } from 'lodash';

/**
 * for SignoTec Pad Doc see https://www.signotec.com/portal/seiten/websocket-pad-server-900000371-10002.html
 */
export class SignoTecService extends InjectableClass {
  constructor(
    $q,
    $window,
    $interval,
    signoTecRequestTokens,
    signoTecResponeTokens,
  ) {
    'ngInject';
    super({
      $q,
      $window,
      $interval,
      signoTecRequestTokens,
      signoTecResponeTokens,
    });

    this.errors = [];
  }

  /*

    Protocoll should look like this:

    TOKEN_CMD_GET_SERVER_VERSION
    TOKEN_CMD_SET_INTERFACE_VERSION
    TOKEN_CMD_SEARCH_FOR_PADS
    
    TOKEN_CMD_OPEN_PAD
    TOKEN_CMD_SIGNATURE_START
    ...
    TOKEN_CMD_SIGNATURE_POINT ^ N
    ...

    TOKEN_CMD_SIGNATURE_CONFIRM
    TOKEN_CMD_SIGNATURE_STOP
  */

  connect() {
    this.status = {
      code: 1,
      message: 'sigpad.connecting',
    };

    let defer = this.$q.defer();

    this.websocket = new WebSocket('wss://local.signotecwebsocket.de:49494');

    let promisePending = true;
    this.websocket.onopen = (event) => {
      // first start a "handshake with the pad"
      this.getServerVersionInfo();
    };
    this.websocket.onmessage = (event) =>
      this.onMessage(angular.fromJson(event.data));
    this.websocket.onerror = (event) => {
      if (promisePending) {
        this.errors.push('websocket error');
        defer.reject();
        promisePending = false;
      }
    };

    return defer.promise;
  }

  onMessage(message) {
    if (
      message.TOKEN_TYPE === 'TOKEN_TYPE_RESPONSE' &&
      message.TOKEN_CMD_ORIGIN
    ) {
      this.onPadActiveAction(message);
    }

    if (message.TOKEN_TYPE === 'TOKEN_TYPE_SEND') {
      this.onSignatureDoneAction(message);
    }
  }

  onPadActiveAction(message) {
    switch (message.TOKEN_CMD_ORIGIN) {
      case 'TOKEN_CMD_OPEN_PAD':
        if (message.TOKEN_PARAM_RETURN_CODE === '0') {
          this.status = {
            code: 0,
            message: 'sigpad.connected',
          };
          this.errorDescription = null;
          this.signatureStart();
          this.killOpenPadInterval();
        } else {
          if (message.TOKEN_PARAM_RETURN_CODE === '-22') {
            this.status = {
              code: -2,
              message: 'sigpad.error.usbDisconnected',
            };
            this.errors.push('sigpad.error.usbDisconnected');
            if (!this.openPadInterval) {
              this.openPadInterval = this.$interval(() => this.openPad(), 1000);
            }
          } else {
            this.status = {
              code: -1,
              message: 'sigpad.error.connectionFailed',
            };
            this.errorDescription = message.TOKEN_PARAM_ERROR_DESCRIPTION;
            this.errors.push('sigpad.error.connectionFailed');
          }
        }
        break;

      case 'TOKEN_CMD_GET_SERVER_VERSION':
        this.setInterfaceVersion();
        break;

      case 'TOKEN_CMD_SET_INTERFACE_VERSION':
        this.searchForPads();
        break;

      case 'TOKEN_CMD_SEARCH_FOR_PADS':
        if (message.TOKEN_PARAM_RETURN_CODE === '0') {
          this.openPad();
        }
        break;

      case 'TOKEN_CMD_SIGNATURE_START':
        if (message.TOKEN_PARAM_RETURN_CODE === '0') {
          this.status = {
            code: 0,
            message: 'sigpad.requestUserSign',
          };
        }
        break;

      case 'TOKEN_CMD_SIGNATURE_IMAGE':
        if (message.TOKEN_PARAM_RETURN_CODE === '0') {
          this.signatureImage = `data:image/png;base64,${message.TOKEN_PARAM_FILE}`;
          this.closePad();
        } else if (message.TOKEN_PARAM_RETURN_CODE === '-17') {
          // user pressed "accept" although he didn't write anything
          this.openPad();
        } else {
          this.status = {
            code: -1,
            message: 'sigpad.error.signData',
          };
          this.sendToPad({
            TOKEN_CMD: 'TOKEN_CMD_SIGNATURE_SIGN_DATA',
          });
        }
        break;

      case 'TOKEN_CMD_SIGNATURE_SIGN_DATA':
        this.signatureData = message.TOKEN_PARAM_SIGNATURE_SIGN_DATA;
        break;

      case 'TOKEN_CMD_SIGNATURE_STOP':
        this.signature = this.getCroppedSignature(
          this.context.canvas.width,
          this.context.canvas.height,
          this.context,
        );
        this.closePad();
        break;

      case 'TOKEN_CMD_CLOSE_PAD':
        if (this.onCloseCallback) {
          this.onCloseCallback();
          this.onCloseCallback = null;
        } else {
          this.status = {
            code: 2,
            message: 'sigpad.connectionClosed',
          };
        }
        break;
    }
  }

  setContext(context) {
    this.context = context;
    this.context.strokeStyle = '#000000';
    this.context.lineJoin = 'round';
    this.context.lineWidth = 5;
  }

  getServerVersionInfo() {
    this.sendToPad({
      TOKEN_CMD: 'TOKEN_CMD_GET_SERVER_VERSION',
    });
  }

  setInterfaceVersion() {
    this.sendToPad({
      TOKEN_CMD: 'TOKEN_CMD_SET_INTERFACE_VERSION',
      TOKEN_PARAM_VERSION: '3.2.0.0',
    });
  }

  searchForPads() {
    this.sendToPad({
      TOKEN_CMD: 'TOKEN_CMD_SEARCH_FOR_PADS',
      TOKEN_PARAM_PAD_SUBSET: 'HID;All',
    });
  }

  closePad() {
    this.sendToPad({
      TOKEN_CMD: 'TOKEN_CMD_CLOSE_PAD',
    });
  }

  sendToPad(message) {
    defaults(message, {
      TOKEN_TYPE: 'TOKEN_TYPE_REQUEST',
      TOKEN_PARAM_PAD_INDEX: 0,
    });
    if (this.websocket && this.websocket.readyState === 1) {
      this.websocket.send(angular.toJson(message));
    }
  }

  disconnect() {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
  }

  resetSignature(context) {
    context.clearRect(
      0,
      0,
      this.context.canvas.width,
      this.context.canvas.height,
    ); // Clears the canvas
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, 4000, 3000);
    context.globalAlpha = 1;

    this.setContext(context);
  }

  clearPad() {
    this.x = undefined;
    this.y = undefined;
    if (this.context) {
      this.resetSignature(this.context);
    }

    this.sendToPad({
      TOKEN_CMD: 'TOKEN_CMD_SIGNATURE_RETRY',
    });
  }

  userConfirm() {
    this.sendToPad({
      TOKEN_CMD: 'TOKEN_CMD_SIGNATURE_STOP',
    });
  }

  getSignature() {
    const message = {
      TOKEN_CMD: 'TOKEN_CMD_SIGNATURE_IMAGE',
      TOKEN_PARAM_FILE_TYPE: 1,
    };
    this.sendToPad(message);
  }

  get signature() {
    return this.signatureImage;
  }

  set signature(val) {
    this.signatureImage = val;
  }

  onSignatureDoneAction(message) {
    const token = {
      SIGNATURE_RETRY: 'TOKEN_CMD_SIGNATURE_RETRY',
      SIGNATURE_CANCEL: 'TOKEN_CMD_SIGNATURE_CANCEL',
    };
    switch (message.TOKEN_CMD) {
      case token.SIGNATURE_RETRY:
      case token.SIGNATURE_CANCEL:
        // user clicked on retry or cancel
        // cancel doesn't make sense for our use case, so let the user just retry
        this.clearPad();
        break;

      case 'TOKEN_CMD_SIGNATURE_CONFIRM':
        // user clicked on the check mark on th epad
        this.userConfirm();
        break;

      case 'TOKEN_CMD_SIGNATURE_POINT':
        if (message.TOKEN_PARAM_POINT.p === '0') {
          this.context.closePath();
          this.x = parseInt(message.TOKEN_PARAM_POINT.x) - 1;
          this.y = parseInt(message.TOKEN_PARAM_POINT.y) - 1;
        }

        this.context.beginPath();
        this.context.moveTo(this.x, this.y);
        this.context.lineTo(
          parseInt(message.TOKEN_PARAM_POINT.x),
          parseInt(message.TOKEN_PARAM_POINT.y),
        );
        this.context.stroke();

        this.x = parseInt(message.TOKEN_PARAM_POINT.x);
        this.y = parseInt(message.TOKEN_PARAM_POINT.y);

        break;

      case 'TOKEN_CMD_DISCONNECT':
        // some idiot pulled out the usb cable... tell the user he is an idiot
        this.status = {
          code: -2,
          message: 'sigpad.error.usbDisconnected',
        };

        if (!this.openPadInterval) {
          this.openPadInterval = this.$interval(() => this.openPad(), 1000);
        }
    }
  }

  killOpenPadInterval() {
    if (!this.openPadInterval) {
      return;
    }
    this.$interval.cancel(this.openPadInterval);
    this.openPadInterval = null;
  }

  openPad() {
    this.onCloseCallback = () => {
      this.status = {
        code: 1,
        message: 'sigpad.connecting',
      };
    };
    this.sendToPad({
      TOKEN_CMD: 'TOKEN_CMD_OPEN_PAD',
    });
    this.signatureStart();
  }

  signatureStart() {
    const message = {
      TOKEN_CMD: 'TOKEN_CMD_SIGNATURE_START',
    };
    this.sendToPad(message);
  }

  getCroppedSignature(width, height, ctx) {
    let image = ctx.getImageData(0, 0, width, height);
    let searchForBlackValue = function (x, y) {
      const rgbIndex = 0; // this index is for RED [1: green; 2: blue; 3: alpha]
      return image.data[(width * y + x) * 4 + rgbIndex] < 255;
    };

    let scanY = function (fromTop) {
      let offset = fromTop ? 1 : -1;

      // loop through each row
      for (
        var y = fromTop ? 0 : height - 1;
        fromTop ? y < height : y > -1;
        y += offset
      ) {
        // loop through each column
        for (var x = 0; x < width; x++) {
          // exit if we find a pixel with other value than [255, 255, 255]
          if (searchForBlackValue(x, y)) {
            return y;
          }
        }
      }
      return null; // all image is white
    };

    let scanX = function (fromLeft) {
      let offset = fromLeft ? 1 : -1;

      // loop through each column
      for (
        var x = fromLeft ? 0 : width - 1;
        fromLeft ? x < width : x > -1;
        x += offset
      ) {
        // loop through each row
        for (var y = 0; y < height; y++) {
          // exit if we find a pixel with other value than [255, 255, 255]
          if (searchForBlackValue(x, y)) {
            return x;
          }
        }
      }
      return null; // all image is white
    };

    let cropTop = scanY(true);
    let cropBottom = scanY(false);
    let cropLeft = scanX(true);
    let cropRight = scanX(false);

    if (!cropTop && !cropBottom && !cropLeft && !cropRight) {
      // canvas is blank
      return null;
    }

    let croppedWidth = cropRight - cropLeft;
    let croppedHeight = cropBottom - cropTop;
    // Make sure the signature has a minimum size to prevent ugly results
    let minimumWidth = 200;
    if (croppedWidth < minimumWidth) {
      cropLeft -= (minimumWidth - croppedWidth) / 2;
      croppedWidth = minimumWidth;
    }
    let aspectRatio = 3 / 2;
    if (croppedWidth / croppedHeight < aspectRatio) {
      let prevCroppedWidth = croppedWidth;
      croppedWidth = croppedHeight * aspectRatio;
      cropLeft -= (croppedWidth - prevCroppedWidth) / 2;
    } else {
      let prevCroppedHeight = croppedHeight;
      croppedHeight = croppedWidth * (1 / aspectRatio);
      cropTop -= (croppedHeight - prevCroppedHeight) / 2;
    }
    let croppedImage = ctx.getImageData(
      cropLeft,
      cropTop,
      croppedWidth,
      croppedHeight,
    );
    // Possible without a new canvas?
    let tmpCanvas = this.$window.document.createElement('canvas');
    tmpCanvas.width = croppedWidth;
    tmpCanvas.height = croppedHeight;
    let context = tmpCanvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, croppedWidth, croppedHeight);
    context.putImageData(croppedImage, 0, 0);
    return tmpCanvas.toDataURL('image/jpeg');
  }
}
